import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { CONTINENT_ORDER, isCountryLevel, sortBySearchMatch } from "@ckgo2/shared";
import type { AdminDestinationListItem, Destination } from "@ckgo2/shared/types";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { useFacetedParams } from "@/hooks/useFacetedParams";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel from "@/components/common/FacetPanel";
import ListSortSelect from "@/components/common/ListSortSelect";
import {
  countBuckets,
  matchesFacet,
  pageSlice,
  compareCzech,
  compareNewest,
} from "@/lib/clientFacets";
import { Plus, MapPin, Edit, Trash2, Star, Copy, Menu } from "lucide-react";
import i18n from "@/i18n";

const CONTINENTS: Record<string, string> = {
  asia: i18n.t("catalog.continents.asia"),
  africa: i18n.t("catalog.continents.africa"),
  "south-america": i18n.t("catalog.continents.southAmerica"),
  "north-america": i18n.t("catalog.continents.northAmerica"),
  oceania: i18n.t("catalog.continents.oceania"),
  europe: i18n.t("catalog.continents.europe"),
};

const PAGE_SIZE = 50;
/** Výchozí řazení A–Z (F1.8); volba „Pořadí" zůstává, ať jde připnutí zkontrolovat. */
const DEFAULT_SORT = "name";
const LEVEL_ORDER = ["country", "place"] as const;
const PUBLISHED_ORDER = ["published", "draft"] as const;
const NAV_ORDER = ["shown", "hidden"] as const;

/**
 * Seznam destinací (země i lokality).
 *
 * Filtry, hledání i řazení jsou ve URL jako u ostatních seznamů adminu. Facet „Země"
 * úmyslně řadí zemi POD SEBE SAMOU (`parentId ?? id`): garant Japonska tak jedním
 * kliknutím uvidí Japonsko i všechny jeho lokality, ne jen lokality bez země.
 *
 * Bez parametru `?sort=` je seznam česky abecedně (`compareCzech`); připnutí (`sortOrder`)
 * ukazuje volba „Pořadí". Hodnoty fasety Země jsou abecedně, výčty v pořadí výčtu.
 */
export default function DestinationsPage() {
  const { t } = useTranslation();
  const { getValues, search, page, params } = useFacetedParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Admin seznam ve štíhlé projekci (sdílený dotaz s roletkami editorů) — nese i
  // nezveřejněné (předseedované) země, které veřejný endpoint skrývá; označuje je čip
  // Koncept níž.
  const { data, isLoading, isFetching, error } = useAdminDestinationList();

  /**
   * Po změně destinace se obnoví oba seznamy: prefix `["admin-destinations"]` zasáhne
   * sdílený štíhlý admin seznam (`ADMIN_DESTINATION_LIST_KEY`), prefix `["destinations"]`
   * veřejný štíhlý seznam publikovaných destinací, ze kterého přiřazují Google recenze.
   */
  const invalidateDestinationLists = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-destinations"] });
    queryClient.invalidateQueries({ queryKey: ["destinations"] });
  };

  // Kopie vzniká jako NEZVEŘEJNĚNÝ koncept se slugem `<slug>-copy` a rovnou se otevře
  // k úpravám — obsah se přepisuje, ne prohlíží. Kontakty a týmy se nekopírují (jsou
  // to konkrétní lidé), stejně jako zájezdy a inspirace, které míří ze své strany.
  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ApiResponse<Destination>>(`/api/admin/destinations/${id}/duplicate`),
    onSuccess: (response) => {
      invalidateDestinationLists();
      if (response.data?.id) navigate(`/destinations/${response.data.id}`);
    },
    onError: (err: Error) => alert(err.message),
  });

  /**
   * Přepínač „nabízet v menu a filtrech" PŘÍMO ZE SEZNAMU.
   *
   * Výběr se dělá po zemi naráz (Vietnam má 21 lokalit a nabízet chceme tři), takže cesta
   * přes detail — otevřít, záložka Základní, uložit, zpět — je pro tuhle jednu operaci
   * dvacetkrát delší než rozhodnutí samo. Posílá se ČÁSTEČNÝ PUT: CRUD routa nastavuje
   * jen poslané klíče, takže se rozpracovaný obsah destinace nemá jak přepsat.
   */
  const navMutation = useMutation({
    mutationFn: ({ id, showInNav }: { id: string; showInNav: boolean }) =>
      api.put<ApiResponse<Destination>>(`/api/admin/destinations/${id}`, { showInNav }),
    onSuccess: () => invalidateDestinationLists(),
    onError: (err: Error) => alert(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/destinations/${id}`),
    onSuccess: () => invalidateDestinationLists(),
    onError: (err: Error) => {
      // Cascade guard (vize C6): the API blocks deleting a destination that still has places
      // or linked trips with 409 `destination_in_use`. The client already rendered its
      // dictionary text with the name and both counts (vize F13.9).
      alert(err.message);
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (confirm(t("catalog.confirmDeleteNamed", { name }))) {
      deleteMutation.mutate(id);
    }
  };

  const destinations = useMemo(() => data?.data ?? [], [data]);
  const sort = params.get("sort") ?? DEFAULT_SORT;
  const continentSel = getValues("continent");
  const countrySel = getValues("country");
  const levelSel = getValues("level");
  const publishedSel = getValues("published");
  const navSel = getValues("nav");

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of destinations) map.set(d.id, d.name);
    return map;
  }, [destinations]);

  /** Země, pod kterou položka patří — u lokality nadřazená, u země ona sama. */
  const countryOf = (d: AdminDestinationListItem) => d.parentId || d.id;
  const levelOf = (d: AdminDestinationListItem) => (d.parentId ? "place" : "country");
  const publishedOf = (d: AdminDestinationListItem) =>
    d.isPublished === false ? "draft" : "published";
  const navOf = (d: AdminDestinationListItem) => (d.showInNav === false ? "hidden" : "shown");

  const facets = useMemo(
    () => ({
      continent: countBuckets(destinations, (d) => d.continent),
      country: countBuckets(destinations, countryOf),
      level: countBuckets(destinations, levelOf),
      published: countBuckets(destinations, publishedOf),
      nav: countBuckets(destinations, navOf),
    }),
    [destinations],
  );

  const filtered = useMemo(() => {
    const rows = destinations.filter(
      (d) =>
        matchesFacet(continentSel, d.continent) &&
        matchesFacet(countrySel, countryOf(d)) &&
        matchesFacet(levelSel, levelOf(d)) &&
        matchesFacet(publishedSel, publishedOf(d)) &&
        matchesFacet(navSel, navOf(d)),
    );
    // Hledání řadí podle shody (Bali před lokalitami, které Bali jen zmiňují), při stejné
    // shodě zemi před lokalitou (sdílený `isCountryLevel`, ne `levelOf` facety — Bali
    // s `showAsCountry` je země), teprve pak podle zvoleného řazení seznamu.
    return sortBySearchMatch(rows, search, {
      name: (d) => d.name,
      other: (d) => [d.slug, d.shortDescription],
      isCountryLevel,
      tieBreak: (a, b) => {
        if (sort === "order")
          // Podle pořadí a při shodě abecedně, ať seznam neposkakuje.
          return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || compareCzech(a.name, b.name);
        if (sort === "recent")
          return compareNewest(a.updatedAt ?? a.createdAt, b.updatedAt ?? b.createdAt);
        // Výchozí („name"): česky abecedně.
        return compareCzech(a.name, b.name);
      },
    });
  }, [destinations, search, continentSel, countrySel, levelSel, publishedSel, navSel, sort]);

  const visible = pageSlice(filtered, page, PAGE_SIZE);

  if (error) {
    return (
      <div className="card text-error-600 text-center">
        {t("catalog.destinations.list.loadError")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("catalog.destinations.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("catalog.destinations.list.subtitle")}
          </p>
        </div>
        <Link to="/destinations/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("catalog.destinations.list.new")}
        </Link>
      </div>

      <FacetedListLayout
        searchPlaceholder={t("catalog.destinations.list.searchPlaceholder")}
        total={filtered.length}
        limit={PAGE_SIZE}
        isLoading={isLoading}
        isFetching={isFetching}
        actions={
          <ListSortSelect
            defaultValue={DEFAULT_SORT}
            options={[
              { value: "name", label: t("shared.listSort.titleAsc") },
              { value: "order", label: t("shared.listSort.orderAsc") },
              { value: "recent", label: t("shared.listSort.recent") },
            ]}
          />
        }
        filters={
          <>
            <FacetPanel
              title={t("catalog.destinations.list.facetCountry")}
              paramKey="country"
              buckets={facets.country}
              labelFor={(v) => nameById.get(v) ?? v}
              order="name"
            />
            <FacetPanel
              title={t("catalog.destinations.list.colContinent")}
              paramKey="continent"
              buckets={facets.continent}
              labelFor={(v) => CONTINENTS[v] ?? v}
              order={{ enum: CONTINENT_ORDER }}
            />
            <FacetPanel
              title={t("catalog.destinations.list.facetLevel")}
              paramKey="level"
              buckets={facets.level}
              order={{ enum: LEVEL_ORDER }}
              labelFor={(v) =>
                v === "country"
                  ? t("catalog.destinations.list.levelCountry")
                  : t("catalog.destinations.list.levelPlace")
              }
            />
            <FacetPanel
              title={t("common.fields.status")}
              paramKey="published"
              buckets={facets.published}
              order={{ enum: PUBLISHED_ORDER }}
              labelFor={(v) =>
                v === "published" ? t("common.status.published") : t("common.status.draft")
              }
            />
            <FacetPanel
              title={t("catalog.destinations.list.facetNav")}
              paramKey="nav"
              buckets={facets.nav}
              order={{ enum: NAV_ORDER }}
              labelFor={(v) =>
                v === "shown"
                  ? t("catalog.destinations.list.navShown")
                  : t("catalog.destinations.list.navHidden")
              }
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {visible.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-gray-500">
              <MapPin className="mb-2 h-12 w-12 text-gray-300" />
              <p>{t("catalog.destinations.list.empty")}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.destination")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("catalog.destinations.list.colContinent")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.sortOrder")}
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.status")}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("common.fields.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visible.map((destination) => (
                  <tr key={destination.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {destination.featuredImage ? (
                          <img
                            src={destination.featuredImage}
                            alt={destination.name}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                            <MapPin className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {destination.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            /{destination.slug}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 dark:text-gray-300">
                        {CONTINENTS[destination.continent]}
                      </span>
                      {/* U lokality je důležitější, pod kterou zemí visí, než kontinent. */}
                      {destination.parentId && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {nameById.get(destination.parentId) ?? destination.parentId}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 dark:text-gray-300">
                        {destination.sortOrder}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap items-center gap-2">
                        {/*
                          Odznak je zároveň PŘEPÍNAČ — výběr do menu se dělá po zemi naráz,
                          takže patří tam, kde je celá země na jedné obrazovce.
                        */}
                        <button
                          type="button"
                          onClick={() =>
                            navMutation.mutate({
                              id: destination.id,
                              showInNav: destination.showInNav === false,
                            })
                          }
                          disabled={navMutation.isPending}
                          title={t("catalog.destinations.list.navToggleTitle")}
                          className={`badge inline-flex items-center gap-1 transition-colors disabled:opacity-50 ${
                            destination.showInNav === false
                              ? "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400"
                              : "bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300"
                          }`}
                        >
                          <Menu className="h-3 w-3" />
                          {destination.showInNav === false
                            ? t("catalog.destinations.list.navHidden")
                            : t("catalog.destinations.list.navShown")}
                        </button>
                        {destination.isPublished === false && (
                          <span className="badge bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            {t("common.status.draft")}
                          </span>
                        )}
                        {destination.isFeatured ? (
                          <span className="badge-primary inline-flex items-center gap-1">
                            <Star className="h-3 w-3" />
                            {t("catalog.destinations.list.featured")}
                          </span>
                        ) : (
                          <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {t("catalog.destinations.list.standard")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => duplicateMutation.mutate(destination.id)}
                          disabled={duplicateMutation.isPending}
                          className="btn-ghost btn-sm"
                          title={t("common.actions.duplicate")}
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <Link
                          to={`/destinations/${destination.id}`}
                          className="btn-ghost btn-sm"
                          title={t("common.actions.edit")}
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(destination.id, destination.name)}
                          disabled={deleteMutation.isPending}
                          className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20"
                          title={t("common.actions.delete")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
